import { useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  depth: number;
  phase: number;
  twinkleSpeed: number;
}

const ConstellationBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    const particles: Particle[] = [];
    const particleCount = 80;
    const connectionDistance = 150;
    const repulseDistance = 200;
    const mouse = { x: -1000, y: -1000 };
    const trail: { x: number; y: number; age: number }[] = [];
    const maxTrailLength = 20;
    const scrollY = { value: 0 };
    const parallaxFactor = 0.4;
    const depthLayers = [0.2, 0.5, 0.9];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const createParticle = (x?: number, y?: number): Particle => {
      const depth = depthLayers[Math.floor(Math.random() * depthLayers.length)];
      const sizeByDepth = depth === 0.2 ? Math.random() * 3 + 3 : depth === 0.5 ? Math.random() * 2 + 2 : Math.random() * 1.5 + 0.5;
      const opacityByDepth = depth === 0.2 ? Math.random() * 0.2 + 0.5 : depth === 0.5 ? Math.random() * 0.2 + 0.3 : Math.random() * 0.15 + 0.15;
      const speedScale = 0.5 + depth * 1.5;
      return {
        x: x ?? Math.random() * canvas.width,
        y: y ?? Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 6 * speedScale,
        vy: (Math.random() - 0.5) * 6 * speedScale,
        size: sizeByDepth,
        opacity: opacityByDepth,
        depth,
        phase: Math.random() * Math.PI * 2,
        twinkleSpeed: 0.01 + Math.random() * 0.03,
      };
    };

    for (let i = 0; i < particleCount; i++) {
      particles.push(createParticle());
    }

    const onMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      trail.push({ x: e.clientX, y: e.clientY, age: 0 });
      if (trail.length > maxTrailLength) trail.shift();
    };

    const onMouseLeave = () => {
      mouse.x = -1000;
      mouse.y = -1000;
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseleave", onMouseLeave);

    const onScroll = () => { scrollY.value = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        // Mouse repulse
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < repulseDistance && dist > 0) {
          const force = (repulseDistance - dist) / repulseDistance;
          p.x += (dx / dist) * force * 5;
          p.y += (dy / dist) * force * 5;
        }

        p.x += p.vx;
        p.y += p.vy;

        // Out mode - wrap around
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;

        // Parallax offset for drawing only
        const drawY = p.y + scrollY.value * p.depth * parallaxFactor;

        // Twinkling animation
        p.phase += p.twinkleSpeed;
        const twinkle = 0.5 + 0.5 * Math.sin(p.phase); // 0 to 1
        const currentOpacity = p.opacity * (0.2 + 0.8 * twinkle); // 20%-100% of base
        const currentSize = p.size * (0.7 + 0.3 * twinkle); // 70%-100% of base

        ctx.beginPath();
        ctx.arc(p.x, drawY, currentSize, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${currentOpacity})`;
        ctx.fill();
      });

      // Connection lines with parallax offsets
      for (let i = 0; i < particles.length; i++) {
        const drawYi = particles[i].y + scrollY.value * particles[i].depth * parallaxFactor;
        for (let j = i + 1; j < particles.length; j++) {
          const drawYj = particles[j].y + scrollY.value * particles[j].depth * parallaxFactor;
          const dx = particles[i].x - particles[j].x;
          const dy = drawYi - drawYj;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDistance) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, drawYi);
            ctx.lineTo(particles[j].x, drawYj);
            const avgDepth = (particles[i].depth + particles[j].depth) / 2;
            const depthDiff = Math.abs(particles[i].depth - particles[j].depth);
            const depthFade = 1 - depthDiff * 0.7;
            ctx.strokeStyle = `rgba(255, 255, 255, ${0.4 * (1 - dist / connectionDistance) * avgDepth * depthFade})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      // Mouse trail
      for (let i = trail.length - 1; i >= 0; i--) {
        trail[i].age++;
        if (trail[i].age > 30) {
          trail.splice(i, 1);
          continue;
        }
        const t = trail[i];
        const alpha = (1 - t.age / 30) * 0.4;
        const radius = (1 - t.age / 30) * 3;
        ctx.beginPath();
        ctx.arc(t.x, t.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 204, 0, ${alpha})`;
        ctx.fill();
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none"
      style={{ width: "100%", height: "100%" }}
    />
  );
};

export default ConstellationBackground;
